import { useState, useEffect } from 'react';
import KPICard, { fmt, fmtPct, calcDelta } from '../components/KPI';
import { CustomerBarChart, PeriodTrendChart, OpportunityChart } from '../components/Charts';
import { fetchData } from '../hooks/useDataRefresh';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const CHANNEL_COLORS = {
  'MAJOR GROCERY': '#3b82f6',
  'MAJOR OTHER': '#f59e0b',
  'EXPORT': '#8b5cf6',
  'INDEPENDENT GROCERY': '#22c55e',
  'PRIVATE LABEL': 'var(--text-muted)',
  'CLEARANCE': '#ef4444',
  'ONLINE': '#06b6d4',
};


const FY_END   = { FY24: '2024-06-30', FY25: '2025-06-30', FY26: '2026-06-30', FY27: '2027-06-30' };
const FY_START = { FY24: '2023-07-01', FY25: '2024-07-01', FY26: '2025-07-01', FY27: '2026-07-01' };
// P11 start: 2 months after last completed period (P8 Feb 2026) = May 2026
const FY26_PIPELINE_FROM = '2026-05-01';

// FY budgets: FY26 is per-customer in data; FY25 = $70M total (distributed by fy25_gsv share)
const FY25_BUDGET_TOTAL = 70_000_000;
const FY_TOTAL_PERIODS = 13; // full financial year periods

// Derive a "Period Ending PX (Mon YYYY)" label from the periods array —
// the last period with TY actuals. Falls back to a blank if periods haven't
// loaded yet. Keeps the KPI sublabel in sync with whatever salesv2-periods.json
// contains after a refresh (was previously hardcoded to P8 Feb 2026).
function lastActualLabel(periods) {
  if (!periods?.length) return '';
  const withActuals = periods.filter(p => p.gsv_ty != null);
  if (!withActuals.length) return '';
  const last = withActuals[withActuals.length - 1];
  const today = new Date();
  // FY starts in July. Periods 1–6 sit in the prior calendar year, 7–12 in current.
  const fyStartYear = today.getMonth() >= 6 ? today.getFullYear() : today.getFullYear() - 1;
  const calYear = last.period <= 6 ? fyStartYear : fyStartYear + 1;
  return `Period Ending ${last.label} (${last.month} ${calYear})`;
}

const PROB_LABEL = { '1. High': 'High', '2. Moderate': 'Moderate', '3. Low': 'Low' };
const PROB_COLOR = { High: 'var(--success)', Moderate: 'var(--warn)', Low: 'var(--text-muted)' };

// Return the right estimate field for a customer given the selected FY
function getEstimate(c, fy) {
  if (fy === 'FY27') return c.fy27_estimate_gsv || 0;
  if (fy === 'FY25') return c.fy25_gsv || 0;        // FY25 = full year actuals
  if (fy === 'FY24') return null;                    // no data
  return c.estimate_gsv || 0;                        // FY26 default
}

function estimateLabel(fy) {
  if (fy === 'FY27') return 'FY27 Plan';
  if (fy === 'FY25') return 'FY25 Actual';
  if (fy === 'FY24') return 'FY24 N/A';
  return 'FY26 Estimate';
}

function estimateSublabel(fy) {
  if (fy === 'FY27') return 'FY27 plan (~8% growth)';
  if (fy === 'FY25') return 'FY25 full year actuals';
  if (fy === 'FY24') return 'Historical data not loaded';
  return 'Actual YTD + Fcst remaining';
}

export default function BusinessOverview() {
  const [customers, setCustomers]   = useState([]);
  const [orders, setOrders]         = useState([]);
  const [opps, setOpps]             = useState([]);
  const [periods, setPeriods]       = useState([]);
  const [group, setGroup]           = useState('All');
  const [fy, setFY]                 = useState('FY26');
  const [probFilter, setProbFilter] = useState('All');
  const [expandedOpp, setExpandedOpp] = useState(null);
  const [niq, setNiq] = useState(null);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [brandData, setBrandData] = useState([]);

  useEffect(() => {
    Promise.all([
      fetchData('salesv2-customers.json'),
      fetchData('salesv2-forward-orders.json'),
      fetchData('salesv2-opportunities.json'),
      fetchData('salesv2-periods.json'),
      fetchData('niq-penetration.json'),
      fetchData('scanv2-brand.json'),
    ]).then(([c, fo, o, p, n, b]) => {
      setCustomers(c); setOrders(fo); setOpps(o); setPeriods(p); setNiq(n); setBrandData(b);
    });
  }, []);

  const groups  = ['All', ...Array.from(new Set(customers.map(c => c.group))).sort()];
  const filtered = group === 'All' ? customers : customers.filter(c => c.group === group);

  const totalTY       = filtered.reduce((s, c) => s + (c.gsv_ty || 0), 0);
  const totalLY       = filtered.reduce((s, c) => s + (c.gsv_ly || 0), 0);
  const totalFY25     = filtered.reduce((s, c) => s + (c.fy25_gsv || 0), 0);
  const totalBudget   = filtered.reduce((s, c) => s + (c.budget_gsv || 0), 0);
  const totalFO       = orders.reduce((s, o) => s + (o.fo_revenue || 0), 0);
  const totalEstimate = filtered.reduce((s, c) => s + (getEstimate(c, fy) || 0), 0);

  // Completed fiscal periods YTD = count of periods carrying TY actuals. Derived
  // from the data (not the wall clock) so the budget pro-rata always matches the
  // actuals coverage exactly — a stale calendar would over-state budget YTD vs
  // actuals and make performance look worse than it is.
  const fy26YtdPeriods = Math.min(periods.filter(p => p.gsv_ty != null).length, FY_TOTAL_PERIODS);

  // Budget YTD comparisons
  const fy26BudgetYTD = totalBudget * (fy26YtdPeriods / FY_TOTAL_PERIODS);
  // FY25 budget: distribute $70M proportionally by fy25_gsv share vs all customers
  const allFY25Total  = customers.reduce((s, c) => s + (c.fy25_gsv || 0), 0);
  const fy25BudgetYTD = allFY25Total > 0
    ? FY25_BUDGET_TOTAL * (totalFY25 / allFY25Total)
    : FY25_BUDGET_TOTAL * (fy26YtdPeriods / FY_TOTAL_PERIODS);

  // Opportunity filtering: FY + P11 start date + probability
  const baseOpps = opps.filter(o => {
    if (!o.close_date) return true;
    if (fy === 'FY26') return o.close_date >= FY26_PIPELINE_FROM && o.close_date <= FY_END.FY26;
    if (fy === 'FY27') return o.close_date >= FY_START.FY27 && o.close_date <= FY_END.FY27;
    return o.fy === fy;
  });

  const filteredOpps = probFilter === 'All'
    ? baseOpps
    : baseOpps.filter(o => PROB_LABEL[o.probability] === probFilter);

  const highMedOpp = baseOpps
    .filter(o => o.probability !== '3. Low' && o.value > 0)
    .reduce((s, o) => s + o.value, 0);

  const sortedOppTable = [...filteredOpps].sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>FY</span>
        {['FY24', 'FY25', 'FY26', 'FY27'].map(f => (
          <button key={f} className={fy === f ? 'pill-active' : 'pill-inactive'} onClick={() => setFY(f)}>{f}</button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Channel</span>
        {groups.map(g => (
          <button key={g} className={group === g ? 'pill-active' : 'pill-inactive'} onClick={() => setGroup(g)} style={{ fontSize: 12 }}>
            {g === 'All' ? 'All' : g.replace(' GROCERY', '').replace('INDEPENDENT', 'IND.').replace('PRIVATE LABEL', 'PRIV. LABEL')}
          </button>
        ))}
      </div>

      {/* KPI Row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {fy === 'FY25' ? (
          <KPICard
            label="Invoiced GSV FY25 Full Year"
            value={fmt(totalFY25)}
            delta={calcDelta(totalFY25, fy25BudgetYTD)}
            deltaLabel="vs FY25 Budget"
            sublabel="Full year FY25 actuals"
          />
        ) : (
          <KPICard
            label="Invoiced GSV YTD"
            value={fmt(totalTY)}
            delta={calcDelta(totalTY, totalLY)}
            deltaLabel="vs LY YTD"
            delta2={calcDelta(totalTY, fy26BudgetYTD)}
            delta2Label="vs Budget YTD"
            sublabel={lastActualLabel(periods)}
          />
        )}
        <KPICard
          label={estimateLabel(fy)}
          value={fy === 'FY24' ? '—' : fmt(totalEstimate)}
          delta={fy === 'FY26' ? calcDelta(totalEstimate, totalBudget) : null}
          deltaLabel="vs Budget"
          accent="var(--ai)"
          sublabel={estimateSublabel(fy)}
        />
        <KPICard label="vs Budget" value={fmt(totalBudget)} delta={calcDelta(totalTY, totalBudget)} deltaLabel="YTD actual vs budget" />
        <KPICard label="Forward Orders" value={fmt(totalFO)} sublabel="Next 13 weeks" accent="var(--info)" />
        <KPICard label="Opportunity Pipeline" value={fmt(highMedOpp)} sublabel={`High + Mod — ${fy} from P11`} accent="var(--warn)" />
      </div>

      {/* NIQ Household Penetration */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>NIQ Household Penetration</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Target: 30% · Source: NielsenIQ Omnishopper · Total Outlets Omni
            {niq ? ` · ${niq.period_latest}` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {(niq?.brands || []).map(b => {
            const omni = b.retailers.find(r => r.retailer === 'Total Outlets Omni');
            if (!omni) return null;
            const TARGET = 30;
            const pct = omni.pen_latest / TARGET * 100;
            const pyPct = omni.pen_ya / TARGET * 100;
            const delta = omni.pen_latest - omni.pen_ya;
            return (
              <div key={b.brand} style={{ flex: 1, background: 'var(--bg-elevated)', borderRadius: 8, padding: '14px 16px', border: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ color: b.color, fontWeight: 600, fontSize: 13 }}>{b.brand}</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 18 }}>{omni.pen_latest.toFixed(1)}%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: 11 }}>
                  <span style={{ color: 'var(--text-muted)' }}>PY {omni.pen_ya.toFixed(1)}%</span>
                  <span style={{ color: delta >= 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 600 }}>
                    {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(2)}pp vs PY
                  </span>
                </div>
                <div style={{ position: 'relative', height: 8, background: 'var(--border)', borderRadius: 4 }}>
                  <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: b.color, borderRadius: 4 }} />
                  <div style={{
                    position: 'absolute', left: `${Math.min(pyPct, 100)}%`, top: -3,
                    width: 2, height: 14, background: 'var(--text-muted)', borderRadius: 1,
                    transform: 'translateX(-1px)',
                  }} title={`PY: ${omni.pen_ya.toFixed(1)}%`} />
                  <div style={{ position: 'absolute', right: 0, top: -4, width: 2, height: 16, background: 'var(--danger)', borderRadius: 1 }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{omni.pen_latest.toFixed(1)}% of {TARGET}% target</span>
                  <span style={{ fontSize: 11, color: 'var(--danger)' }}>{(TARGET - omni.pen_latest).toFixed(1)}pp gap</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Charts Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
              {fy === 'FY25' ? 'Customer GSV — FY25 Full Year' : 'Customer GSV — YTD TY vs YTD LY'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {fy === 'FY25' ? 'Full year FY25 actuals' : lastActualLabel(periods)}
            </div>
          </div>
          <CustomerBarChart
            data={fy === 'FY25'
              ? filtered.map(c => ({ ...c, gsv_ty: c.fy25_gsv || 0, gsv_ly: 0 }))
              : fy === 'FY27'
              ? filtered.map(c => ({ ...c, gsv_ty: c.fy27_estimate_gsv || 0, gsv_ly: c.estimate_gsv || 0 }))
              : filtered
            }
          />
        </div>
        <div className="card">
          <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>Period Sales Trend (FY26)</div>
          <PeriodTrendChart data={periods} />
        </div>
      </div>

      {/* MAT Channel Mix + Brand Mix */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ChannelPieChart customers={customers} fy={fy} />
        <BrandMix brandData={brandData} />
      </div>

      {/* Opportunity Pipeline — full width */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Opportunity Pipeline</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
              {fy === 'FY26' ? 'From P11 (May 2026) — 2 months after last completed period' : `${fy} opportunities`}
              {' · '}{filteredOpps.length} shown
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Probability</span>
            {['All', 'High', 'Moderate', 'Low'].map(p => (
              <button key={p} className={probFilter === p ? 'pill-active' : 'pill-inactive'} onClick={() => setProbFilter(p)} style={{ fontSize: 12 }}>{p}</button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' }}>
          <OpportunityChart data={filteredOpps} />

          {/* Opportunity detail table */}
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Type</th>
                  <th>Probability</th>
                  <th style={{ textAlign: 'right' }}>Value</th>
                </tr>
              </thead>
              <tbody>
                {sortedOppTable.map((o, i) => {
                  const probKey = PROB_LABEL[o.probability] || 'Low';
                  const isExpanded = expandedOpp === i;
                  return [
                    <tr
                      key={`row-${i}`}
                      onClick={() => setExpandedOpp(isExpanded ? null : i)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ color: 'var(--text-primary)' }}>{o.customer}</td>
                      <td style={{ color: 'var(--text-body)', fontSize: 12 }}>{o.product || '—'}</td>
                      <td style={{ color: 'var(--text-body)', fontSize: 12 }}>{o.type}</td>
                      <td>
                        <span style={{
                          fontSize: 11, fontWeight: 600, color: PROB_COLOR[probKey],
                          background: `color-mix(in srgb, ${PROB_COLOR[probKey]} 14%, transparent)`, borderRadius: 4, padding: '2px 6px',
                        }}>{probKey}</span>
                      </td>
                      <td style={{
                        textAlign: 'right', fontWeight: 600,
                        color: o.value < 0 ? 'var(--danger)' : 'var(--text-primary)',
                      }}>{fmt(o.value)}</td>
                    </tr>,
                    isExpanded && (
                      <tr key={`detail-${i}`}>
                        <td colSpan={5} style={{ background: 'var(--bg-elevated)', padding: '10px 14px' }}>
                          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 12 }}>
                            <div>
                              <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Customer</div>
                              <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{o.customer}</div>
                            </div>
                            <div>
                              <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Product</div>
                              <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{o.product || '—'}</div>
                            </div>
                            <div>
                              <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Type</div>
                              <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{o.type}</div>
                            </div>
                            <div>
                              <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Probability</div>
                              <div style={{ color: PROB_COLOR[probKey], fontWeight: 600 }}>{o.probability}</div>
                            </div>
                            <div>
                              <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Value (GSV)</div>
                              <div style={{ color: o.value < 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 700, fontSize: 14 }}>{fmt(o.value)}</div>
                            </div>
                            <div>
                              <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Financial Year</div>
                              <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{o.fy}</div>
                            </div>
                            {o.close_date && (
                              <div>
                                <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Period</div>
                                <div style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                                  {new Date(o.close_date).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })}
                                </div>
                              </div>
                            )}
                          </div>
                          {o.value < 0 && (
                            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--danger)', background: 'var(--danger-soft)', borderRadius: 4, padding: '4px 10px', display: 'inline-block' }}>
                              Risk item — negative value represents potential revenue at risk
                            </div>
                          )}
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
            {sortedOppTable.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 12 }}>
                No opportunities match the selected filters.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Customer P&L Table */}
      <div className="card">
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>Customer Summary</div>
        <div style={{ overflowX: 'auto' }}>
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Customer</th>
                <th>Channel</th>
                <th style={{ textAlign: 'right' }}>GSV YTD TY</th>
                <th style={{ textAlign: 'right' }}>GSV YTD LY</th>
                <th style={{ textAlign: 'right' }}>Δ%</th>
                <th style={{ textAlign: 'right' }}>Budget</th>
                <th style={{ textAlign: 'right' }}>{estimateLabel(fy)}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.filter(c => (c.gsv_ty || 0) + (c.gsv_ly || 0) > 0).map(c => {
                const delta = calcDelta(c.gsv_ty, c.gsv_ly);
                const est = getEstimate(c, fy);
                const isExp = expandedCustomer === c.customer;

                // Brand breakdown for WW and Coles from SCANv2
                const custKey = c.customer === 'Woolworths Limited' ? 'ww' : c.customer === 'Coles Supermarkets' ? 'coles' : null;
                const brands = custKey ? brandData.map(b => ({
                  family: b.family,
                  ty: b[`${custKey}_ytd_ty`] || 0,
                  ly: b[`${custKey}_ytd_ly`] || 0,
                })) : null;

                return [
                  <tr key={c.customer}
                    onClick={() => setExpandedCustomer(isExp ? null : c.customer)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td style={{ color: 'var(--text-muted)', fontSize: 12, width: 20 }}>{isExp ? '▾' : '▸'}</td>
                    <td style={{ color: 'var(--text-primary)' }}>
                      {c.customer.replace(' Limited', '').replace(' Supermarkets', '').replace(' Australia', '').replace(' Trading Ltd Australia', '')}
                    </td>
                    <td>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)', background: 'var(--border)', borderRadius: 4, padding: '2px 6px' }}>
                        {c.group.replace(' GROCERY', '').replace('INDEPENDENT', 'IND.')}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--text-primary)', fontWeight: 500 }}>{fmt(c.gsv_ty)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-body)' }}>{fmt(c.gsv_ly)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {delta != null && <span className={delta >= 0 ? 'positive' : 'negative'}>{fmtPct(delta)}</span>}
                    </td>
                    <td style={{ textAlign: 'right', color: 'var(--text-body)' }}>{fmt(c.budget_gsv)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--ai)', fontWeight: 500 }}>{est != null ? fmt(est) : '—'}</td>
                  </tr>,
                  isExp && (
                    <tr key={`${c.customer}-detail`}>
                      <td colSpan={8} style={{ background: 'var(--bg-elevated)', padding: '12px 20px', borderBottom: '1px solid var(--border)' }}>
                        {brands ? (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              Brand Performance — YTD (SCANv2 Retail Sales)
                            </div>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                              {brands.map(b => {
                                const d = calcDelta(b.ty, b.ly);
                                const COLOR = { Bhuja: '#f59e0b', Infuzions: '#3b82f6', 'Infuzions Multipack': '#8b5cf6' };
                                return (
                                  <div key={b.family} style={{ background: 'var(--bg-elevated)', borderRadius: 8, padding: '12px 16px', border: '1px solid var(--border)', minWidth: 160 }}>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: COLOR[b.family] || 'var(--text-primary)', marginBottom: 6 }}>
                                      {b.family.replace('Infuzions Multipack', 'Infuzions MP')}
                                    </div>
                                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{fmt(b.ty)}</div>
                                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>LY {fmt(b.ly)}</div>
                                    {d != null && (
                                      <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4, color: d >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                                        {d >= 0 ? '+' : ''}{d.toFixed(1)}% vs LY
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                              Source: SCANv2 retail scan data · Assortment-level data available in NAM Sales tab
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}>
                            Brand-level retail scan data available for Woolworths and Coles via SCANv2.
                            {c.gsv_ty > 100000 && ' For this customer, review performance in the NAM Sales or Forecast tabs.'}
                          </div>
                        )}
                      </td>
                    </tr>
                  ),
                ];
              })}
              <tr style={{ background: 'var(--bg-elevated)', fontWeight: 700 }}>
                <td />
                <td style={{ color: 'var(--text-primary)' }}>TOTAL</td>
                <td />
                <td style={{ textAlign: 'right', color: 'var(--info)', fontWeight: 700 }}>{fmt(totalTY)}</td>
                <td style={{ textAlign: 'right', color: 'var(--text-body)' }}>{fmt(totalLY)}</td>
                <td style={{ textAlign: 'right' }}>
                  <span className={calcDelta(totalTY, totalLY) >= 0 ? 'positive' : 'negative'}>{fmtPct(calcDelta(totalTY, totalLY))}</span>
                </td>
                <td style={{ textAlign: 'right', color: 'var(--text-body)' }}>{fmt(totalBudget)}</td>
                <td style={{ textAlign: 'right', color: 'var(--ai)', fontWeight: 700 }}>{fy !== 'FY24' ? fmt(totalEstimate) : '—'}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          SALESv2 [INVOICED] — actual YTD invoiced GSV to Period 8 (Feb 2026). Budget and estimate fields from planning model. FY27 Plan derived from FY26 estimate at ~8% growth.
        </div>
      </div>
    </div>
  );
}

function ChannelPieChart({ customers, fy }) {
  const [drillChannel, setDrillChannel] = useState(null);
  const estKey = fy === 'FY27' ? 'fy27_estimate_gsv' : fy === 'FY25' ? 'fy25_gsv' : 'estimate_gsv';
  const pyKey  = fy === 'FY27' ? 'estimate_gsv' : fy === 'FY26' ? 'fy25_gsv' : null;

  const byChannel = {};
  customers.forEach(c => {
    const g = c.group;
    if (!byChannel[g]) byChannel[g] = { cur: 0, py: 0 };
    byChannel[g].cur += c[estKey] || 0;
    if (pyKey) byChannel[g].py += c[pyKey] || 0;
  });

  const total = Object.values(byChannel).reduce((s, v) => s + v.cur, 0);
  const data = Object.entries(byChannel)
    .sort((a, b) => b[1].cur - a[1].cur)
    .map(([name, v]) => ({
      name: name.replace(' GROCERY', '').replace('INDEPENDENT', 'IND.'),
      full: name,
      value: v.cur,
      py: v.py,
      pct: total > 0 ? ((v.cur / total) * 100).toFixed(1) : '0.0',
      growth: (pyKey && v.py > 0) ? ((v.cur - v.py) / v.py * 100) : null,
    }));

  const label = fy === 'FY27' ? 'FY27 Plan' : fy === 'FY25' ? 'FY25 Actual' : 'FY26 Estimate';

  // Drilldown: customers within the selected channel
  const drillData = drillChannel ? (() => {
    const channelCusts = customers.filter(c => c.group === drillChannel);
    const channelTotal = channelCusts.reduce((s, c) => s + (c[estKey] || 0), 0);
    return channelCusts
      .filter(c => (c[estKey] || 0) > 0)
      .sort((a, b) => (b[estKey] || 0) - (a[estKey] || 0))
      .map(c => {
        const val = c[estKey] || 0;
        const py  = pyKey ? (c[pyKey] || 0) : 0;
        return {
          name: c.customer.replace(' Limited', '').replace(' Supermarkets', '').replace(' Australia', '').replace(' Trading Ltd Australia', ''),
          value: val,
          py,
          pct: channelTotal > 0 ? ((val / channelTotal) * 100).toFixed(1) : '0.0',
          pctTotal: total > 0 ? ((val / total) * 100).toFixed(1) : '0.0',
          growth: (pyKey && py > 0) ? ((val - py) / py * 100) : null,
        };
      });
  })() : null;

  const activeChannel = drillChannel ? data.find(d => d.full === drillChannel) : null;

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        {drillChannel && (
          <button
            onClick={() => setDrillChannel(null)}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, padding: '0 4px 0 0' }}
          >
            ← All Channels
          </button>
        )}
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
          {drillChannel ? `${activeChannel?.name || drillChannel} — Customer Breakdown` : 'MAT Sales by Channel'}
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
        {label}{drillChannel ? ` · ${activeChannel?.pct}% of total · ${fmt(activeChannel?.value)}` : ` — ${fmt(total)} total`}
        {!drillChannel && <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>· click a channel to drill down</span>}
      </div>

      {!drillChannel ? (
        <>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={70}
                innerRadius={36}
                onClick={d => setDrillChannel(d.full)}
                style={{ cursor: 'pointer' }}
              >
                {data.map((d, i) => (
                  <Cell key={i} fill={CHANNEL_COLORS[d.full] || 'var(--text-muted)'} />
                ))}
              </Pie>
              <Tooltip
                formatter={(v, n) => [fmt(v), n]}
                contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-soft)', borderRadius: 8, fontSize: 11 }}
              />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            {data.map(d => (
              <div
                key={d.name}
                onClick={() => setDrillChannel(d.full)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer', borderRadius: 4, padding: '2px 4px', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: 8, height: 8, borderRadius: 2, background: CHANNEL_COLORS[d.full] || 'var(--text-muted)', flexShrink: 0 }} />
                <span style={{ color: 'var(--text-body)', flex: 1 }}>{d.name}</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{d.pct}%</span>
                <span style={{ color: 'var(--text-muted)', width: 52, textAlign: 'right' }}>{fmt(d.value)}</span>
                {d.growth != null && (
                  <span style={{ width: 48, textAlign: 'right', fontWeight: 600, color: d.growth >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                    {d.growth >= 0 ? '+' : ''}{d.growth.toFixed(1)}%
                  </span>
                )}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {drillData.map(d => (
            <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: CHANNEL_COLORS[drillChannel] || 'var(--text-muted)', flexShrink: 0 }} />
              <span style={{ color: 'var(--text-primary)', flex: 1, fontWeight: 500 }}>{d.name}</span>
              <span style={{ color: 'var(--text-body)' }}>{d.pct}% of channel</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>{d.pctTotal}% of total</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600, width: 58, textAlign: 'right' }}>{fmt(d.value)}</span>
              {d.growth != null && (
                <span style={{ width: 48, textAlign: 'right', fontWeight: 600, color: d.growth >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                  {d.growth >= 0 ? '+' : ''}{d.growth.toFixed(1)}%
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BrandMix({ brandData }) {
  const BRAND_COLORS = { Bhuja: '#f59e0b', Infuzions: '#3b82f6', 'Infuzions Multipack': '#8b5cf6' };

  const brands = brandData.map(b => ({
    name: b.family,
    ty: (b.ww_ytd_ty || 0) + (b.coles_ytd_ty || 0),
    ly: (b.ww_ytd_ly || 0) + (b.coles_ytd_ly || 0),
  }));

  const totalTY = brands.reduce((s, b) => s + b.ty, 0);
  const totalLY = brands.reduce((s, b) => s + b.ly, 0);

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Brand Mix</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>SCANv2 YTD — WW + Coles retail scan</div>
      </div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
        {brands.map(b => (
          <BrandCard key={b.name} name={b.name.replace('Infuzions Multipack', 'Infuzions MP')} ty={b.ty} ly={b.ly} color={BRAND_COLORS[b.name] || 'var(--text-muted)'} />
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {brands.map(b => {
          const pct = totalTY > 0 ? (b.ty / totalTY * 100) : 0;
          return (
            <div key={b.name}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 11 }}>
                <span style={{ color: BRAND_COLORS[b.name] || 'var(--text-muted)', fontWeight: 600 }}>
                  {b.name.replace('Infuzions Multipack', 'Infuzions MP')}
                </span>
                <span style={{ color: 'var(--text-body)' }}>{pct.toFixed(1)}% of total</span>
              </div>
              <div style={{ height: 6, background: 'var(--border)', borderRadius: 3 }}>
                <div style={{ width: `${pct}%`, height: '100%', background: BRAND_COLORS[b.name] || 'var(--text-muted)', borderRadius: 3 }} />
              </div>
            </div>
          );
        })}
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Total YTD: {fmt(totalTY)} TY · {fmt(totalLY)} LY
        </div>
      </div>
    </div>
  );
}

function BrandCard({ name, ty, ly, color }) {
  const delta = calcDelta(ty, ly);
  return (
    <div style={{ flex: 1, background: 'var(--bg-elevated)', borderRadius: 8, padding: 16, border: '1px solid var(--border)' }}>
      <div style={{ color: color, fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{name}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{fmt(ty)}</div>
      {delta != null && (
        <span style={{ fontSize: 12, fontWeight: 600, color: delta >= 0 ? 'var(--success)' : 'var(--danger)' }}>
          {fmtPct(delta)} vs LY YTD
        </span>
      )}
    </div>
  );
}
